import {
	BidSuretyBondClosedReason,
	ContractSuretyType,
	Dtos,
	FinalContractSuretyBondClosedReason,
	SuretyAccountId,
	SuretyBondId,
	SuretyBondType,
	SuretyType,
} from "@inrev/common";
import { useAdminCloseBond } from "@portal/api/bond/admin";
import { FormComponent, FormField, FormFieldGroup, FormItemLabel } from "@portal/components/form";
import { FormCurrencyComponent, FormDateComponent } from "@portal/components/form/fieldLayouts";
import { FormDropdown, FormTextArea } from "@portal/components/form/fields";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Icon, PartialIndicator } from "@portal/components/primitives";
import { Button } from "@portal/components/primitives/Button";
import { ReactNode, useMemo, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { match } from "ts-pattern";
import { bondTypeLabelMap } from "../../../../constants";
import { stripEmptyResolver, typedZodResolver } from "../../../../utils/form";

type AdminCloseBondModalProps = {
	bond: {
		id: SuretyBondId;
		accountId: SuretyAccountId;
		suretyType: SuretyType;
		contractSuretyType: ContractSuretyType;
		bondType: SuretyBondType;
		amount: number;
		number: string;
	};
	button: ReactNode;
};

type AdminCloseBondFormData = {
	suretyType: SuretyType;
	contractSuretyType: ContractSuretyType;
	closedReason: FinalContractSuretyBondClosedReason | BidSuretyBondClosedReason | "";
	closedNote: string | "";
	actualCompletionDate: string | "";
	actualContractAmount: string | "";
};

const getDefaultFormData = (bond: AdminCloseBondModalProps["bond"]): AdminCloseBondFormData => ({
	suretyType: bond.suretyType,
	contractSuretyType: bond.contractSuretyType,
	closedReason: "",
	closedNote: "",
	actualCompletionDate: "",
	actualContractAmount: "",
});

const getClosedReasonOptions = (suretyBondType: SuretyBondType) => {
	return match(suretyBondType as SuretyBondType)
		.with(SuretyBondType.bid, () => [
			{ value: BidSuretyBondClosedReason.bid_lost, label: "Bid lost" },
			{ value: BidSuretyBondClosedReason.not_awarded, label: "Project was not awarded" },
			{
				value: FinalContractSuretyBondClosedReason.returned_by_obligee,
				label: "Returned by obligee",
			},
			{ value: FinalContractSuretyBondClosedReason.input_error, label: "Input error" },
			{ value: BidSuretyBondClosedReason.other, label: "Other" },
		])
		.with(SuretyBondType.final, () => [
			{ value: FinalContractSuretyBondClosedReason.project_completed, label: "Project completed" },
			{ value: FinalContractSuretyBondClosedReason.expired, label: "Term expired" },
			{ value: FinalContractSuretyBondClosedReason.non_payment, label: "Premium non-payment" },
			{ value: FinalContractSuretyBondClosedReason.replaced, label: "Replaced" },
			{
				value: FinalContractSuretyBondClosedReason.returned_by_obligee,
				label: "Returned by obligee",
			},
			{ value: FinalContractSuretyBondClosedReason.input_error, label: "Input error" },
			{ value: FinalContractSuretyBondClosedReason.other, label: "Other" },
		])
		.otherwise(() => []);
};

export const AdminCloseBondModal = ({ bond, button }: AdminCloseBondModalProps) => {
	const { closeBond, closeBondLoading } = useAdminCloseBond(bond.id, bond.accountId);
	const [isOpen, setIsOpen] = useState(false);
	const formMethods = useForm<AdminCloseBondFormData, any, Dtos.Admin.SuretyBond.Close.Request>({
		defaultValues: getDefaultFormData(bond),
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(typedZodResolver(Dtos.Admin.SuretyBond.Close.Request.schema)),
	});
	const widenedFormMethods: UseFormReturn<AdminCloseBondFormData, any, any> = formMethods;
	const closedReasonOptions = useMemo(() => getClosedReasonOptions(bond.bondType), [bond.bondType]);
	const closedReason = formMethods.watch("closedReason");

	const handleSubmit = (data: Dtos.Admin.SuretyBond.Close.Request) => {
		closeBond(data);
		setIsOpen(false);
	};

	return (
		<>
			<span onClick={() => setIsOpen(true)}>{button}</span>
			{isOpen && (
				<Modal onClickOutside={() => setIsOpen(false)}>
					<ModalItemWithHeader
						header={`Close ${bondTypeLabelMap[bond.bondType]} Bond ${bond.number}`}
						className="w-[500px]"
						bodyClassName="p-[30px] pt-[26px]"
						onClose={() => setIsOpen(false)}
					>
						<form>
							<FormFieldGroup>
								<FormField id="closedReason">
									<FormItemLabel className="w-full" noSpace>
										<div className="flex w-full items-center justify-between gap-2">
											<span>Reason for Closing</span>
											<span className="flex items-center gap-1 pt-0.5">
												<PartialIndicator active />
												<span className="text-xs font-semibold text-tertiary">
													Visible to Agent
												</span>
											</span>
										</div>
									</FormItemLabel>
									<FormDropdown
										control={widenedFormMethods.control}
										name="closedReason"
										options={closedReasonOptions}
										placeholder="Select one"
									/>
								</FormField>
								{closedReason === FinalContractSuretyBondClosedReason.project_completed && (
									<>
										<FormDateComponent
											formMethods={formMethods}
											name="actualCompletionDate"
											label="Project Completion Date"
										/>
										<FormCurrencyComponent
											formMethods={formMethods}
											name="actualContractAmount"
											label="Contract Amount at Project Completion"
										/>
									</>
								)}
								<FormComponent formMethods={formMethods} name="closedNote" label="Note">
									<FormTextArea
										control={formMethods.control}
										name="closedNote"
										placeholder="Enter note here"
										className="h-[100px]"
									/>
								</FormComponent>
							</FormFieldGroup>
							<div className="flex justify-center mt-[35px] gap-2">
								<Button color="secondary" variant="outline" onClick={() => setIsOpen(false)}>
									Cancel
								</Button>
								<Button
									color="primary"
									disabled={closedReason === ""}
									leadingIcon={<Icon type="shield-slash" className="stroke-[1]" size="mini" />}
									onClick={formMethods.handleSubmit(handleSubmit)}
								>
									Close Bond
								</Button>
							</div>
						</form>
					</ModalItemWithHeader>
				</Modal>
			)}
			{closeBondLoading && !isOpen && <LoadingModal />}
		</>
	);
};
